import React, { FunctionComponent, useMemo } from 'react';
import { Box } from '@material-ui/core';
import { makeStyles, createStyles, Theme } from '@material-ui/core/styles';
import AddPetAndServiceOption from './AddPetAndServiceOption';
import { Moment } from 'moment-timezone';
import { Button } from 'components/UI';
import AppointmentCard from './AppointmentCard';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import { PetsState } from '@spike/pets-action';
import Pet from '@spike/pet-model';
import { AppointmentMultiple, ItemData, itemTypes, PetServiceWrapper } from 'model/NewMultipleBooking';
import { Product } from '@spike/product-model';
import AddOnService from '@spike/addon-service-model';

interface Props {
    date: Moment;
    multipleAppointments: Array<AppointmentMultiple>;
    onAddAnotherPet: () => void;
    onAddService: (value: number) => void;
    onAddAddOn: (petId: number) => void;
    onAddProduct: (petId: number) => void;
    onAddCustomItem: (petId: number) => void;
    onDelete: (petId: number) => void;
    onEdit: (
        petId: number,
        dateEdit: Moment,
        staffId?: number,
        itemData?: ItemData,
        service?: PetServiceWrapper,
        product?: Product,
        addOn?: AddOnService,
        custom?: string,
        type?: itemTypes
    ) => void;
    onDeleteAppointment: (petId: number, value?: number | string, type?: itemTypes, dateToRemove?: Moment) => void;
    onShowActions: (
        petId: number,
        dateEdit: Moment,
        itemData?: ItemData,
        service?: PetServiceWrapper,
        staffId?: number,
        product?: Product,
        addOn?: AddOnService,
        custom?: string,
        type?: itemTypes
    ) => void;
    onShowPetCard?: (petId: number) => void;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        addAnotherPet: {
            cursor: 'pointer',
            color: '#92B4A7',
            fontWeight: 600,
            fontSize: 14,
            lineHeight: '27px',
            marginTop: 10
        },
        button: {
            'height': 43,
            'width': 168,
            'backgroundColor': '#92B4A7 !important',
            '&:hover': {
                backgroundColor: '#92B4A7 !important'
            },
            'fontSize': 14,
            'lineHeight': '27px',
            'fontWeight': 600,
            '& .MuiButton-label': {
                fontSize: '14px !important'
            }
        }
    })
);

export const MultiplePets: FunctionComponent<Props> = props => {
    const classes = useStyles();

    const petsStateFilter = useSelector<RootState, PetsState>(state => state.pets);

    const pets = useMemo(() => {
        return petsStateFilter.list.filter(pet => !pet.deleted && !pet.deceased);
    }, [petsStateFilter.list]);

    const havePetToAddService = () => {
        let petFounded = false;
        pets.forEach((pet: Pet) => {
            const appoitnemntFounded = props.multipleAppointments.some(
                (appt: AppointmentMultiple) => appt.petId === pet.id
            );
            if (!appoitnemntFounded) {
                petFounded = true;
            }
        });
        return petFounded;
    };

    return (
        <Box>
            {props.multipleAppointments.length === 0 && (
                <AddPetAndServiceOption onAddPetAndServices={props.onAddAnotherPet} />
            )}
            {props.multipleAppointments.map((app: AppointmentMultiple) => (
                <AppointmentCard
                    key={app.petId}
                    date={props.date}
                    appointment={app}
                    onAddService={() => props.onAddService(app.petId)}
                    onDelete={props.onDelete}
                    onEdit={props.onEdit}
                    onDeleteAppointment={props.onDeleteAppointment}
                    onShowActions={props.onShowActions}
                    onAddAddOn={props.onAddAddOn}
                    onAddProduct={props.onAddProduct}
                    onAddCustomItem={props.onAddCustomItem}
                    onShowPetCard={props.onShowPetCard}
                />
            ))}
            {props.multipleAppointments.length > 0 && havePetToAddService() && (
                <Button
                    label="+ Add Another Pet"
                    className={classes.button}
                    onClick={props.onAddAnotherPet}
                />
            )}
        </Box>
    );
};

export default MultiplePets;
